import {
  faBell,
  faComment,
  faGamepad,
  faHeart,
  faNewspaper,
  faReply,
  faStar,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { Notification } from "../contexts/NotificationsContext";
import { resolveImageSrc } from "./image";

export function timeAgo(dateStr: string) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export type NotificationTypeMeta = {
  icon: IconDefinition;
  color: string;
  label: string;
};

const TYPE_META: Record<Notification["type"], NotificationTypeMeta> = {
  new_comment:           { icon: faComment,   color: "text-signal dark:text-[#8DB4FF]",   label: "Comment" },
  new_reply:             { icon: faReply,     color: "text-signal dark:text-[#8DB4FF]", label: "Reply" },
  new_like:              { icon: faHeart,     color: "text-red-500",    label: "Like" },
  new_content:           { icon: faGamepad,   color: "text-green-500",  label: "Game Update" },
  new_franchise_content: { icon: faNewspaper, color: "text-score", label: "Franchise Update" },
  game_published:        { icon: faStar,      color: "text-score", label: "Now Available" },
};

const FALLBACK_META: NotificationTypeMeta = {
  icon: faBell,
  color: "text-gray-400",
  label: "Notification",
};

export const getNotificationTypeMeta = (type: Notification["type"]) =>
  TYPE_META[type] ?? FALLBACK_META;

const CONTENT_TYPES: Notification["type"][] = [
  "new_content",
  "new_franchise_content",
  "game_published",
];

/** Types triggered by another user, where showing who acted is the point. */
const ACTOR_TYPES: Notification["type"][] = ["new_comment", "new_reply", "new_like"];

export const isActorNotification = (n: Notification) => ACTOR_TYPES.includes(n.type);

export const getActorInitial = (n: Notification) =>
  n.triggeredByName?.trim()?.charAt(0)?.toUpperCase() || "?";

export const hasContentThumbnail = (n: Notification) =>
  !!n.contentThumbnail && CONTENT_TYPES.includes(n.type);

/**
 * Content thumbnails are authored at 16:9, so they get a trailing preview slot
 * rather than being cropped into the leading avatar circle.
 */
export const getContentThumbnail = (n: Notification) =>
  hasContentThumbnail(n) ? resolveImageSrc(n.contentThumbnail) : "";

export const getThumbnailAlt = (n: Notification) =>
  n.contentTitle || n.gameTitle || "";
